/**
 * Reading a project's own files — UX-05.
 *
 * The route has existed since storage shipped, with an MCP tool
 * (`list_files`) and a CLI command (`nimbly files`) in front of it. The
 * dashboard had neither: an agent and a terminal could list a project's
 * files and the page called "Storage" could show buckets and policies and
 * not one object.
 *
 * The decisions live here because they are worth testing and the React
 * around them is not.
 */

export type StoredObject = {
  key: string
  size: number
  uploaded: string
  contentType: string | null
}

export type Listing = {
  /** Sub-prefixes under the current one, as a person would read them. */
  folders: string[]
  /** Objects that live directly at the current prefix. */
  files: StoredObject[]
}

/**
 * R2 has no folders. People do.
 *
 * Keys are flat strings and `a/b/c.png` is one object, not three nested
 * things - but somebody who uploaded under `user-123/` is looking for
 * `user-123/`, so the segment after the current prefix becomes a folder to
 * step into. Nothing is moved or renamed; this is a reading of the keys.
 */
export function browse(objects: StoredObject[], prefix: string): Listing {
  const folders = new Set<string>()
  const files: StoredObject[] = []
  for (const object of objects) {
    if (!object.key.startsWith(prefix)) continue
    const rest = object.key.slice(prefix.length)
    if (rest === '') continue
    const slash = rest.indexOf('/')
    if (slash === -1) files.push(object)
    // A key ending in '/' is a marker some tools write for an empty
    // folder. It is a folder here and never a zero-byte file.
    else folders.add(rest.slice(0, slash + 1))
  }
  return { folders: [...folders].sort(), files: files.sort((a, b) => a.key.localeCompare(b.key)) }
}

/** Every prefix above this one, for walking back up. */
export function crumbs(prefix: string): { label: string; prefix: string }[] {
  const parts = prefix.split('/').filter((p) => p !== '')
  const trail: { label: string; prefix: string }[] = [{ label: 'All files', prefix: '' }]
  let at = ''
  for (const part of parts) {
    at += `${part}/`
    trail.push({ label: part, prefix: at })
  }
  return trail
}

/**
 * Two different empty screens.
 *
 * "This project has no files" and "nothing is stored under this prefix"
 * lead somewhere different - the first to uploading anything at all, the
 * second back up a level - and showing the first when somebody has merely
 * stepped into an empty folder reads as data loss.
 */
export function emptyMessage(prefix: string): string {
  return prefix === ''
    ? 'No files yet. Anything your app uploads through the storage API shows up here.'
    : `Nothing is stored under ${prefix}`
}

/** Bytes, at the precision somebody reading a file list actually wants. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  // One decimal below 10 so 1.4 MB does not read as 1 MB, none above it
  // where the extra digit is noise.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`
}

/**
 * What this page deliberately does not offer.
 *
 * The control plane has a route to LIST objects and none to read, write or
 * delete one - those live on the data plane behind a project key, which a
 * dashboard session is not and must not become. Saying so is better than
 * buttons that cannot work, and better than leaving somebody to conclude
 * the files are unreachable.
 */
export const NO_TRANSFER_NOTE =
  'Uploading, downloading and deleting go through your project keys, not this page — a dashboard '
  + 'session is not a project credential. Use the SDK, the CLI (nimbly files), or a signed URL.'
